<template>
	<div class="multi-format-aspect-ratio-selector">
		<Title
			v-if="showHeader"
			class="multi-format-aspect-ratio-selector__header"
			:title="$I18n.trans('event.multiformat_editor.labels.format_title')"
			:subtitle="$I18n.trans('event.multiformat_editor.labels.format_description')"
		/>
		<div
			class="multi-format-aspect-ratio-selector__options"
			:aria-label="$I18n.trans_choice('spotlight.graphics.format', 1)"
		>
			<button
				v-for="option in normalizedOptions"
				:key="option.value"
				type="button"
				class="multi-format-aspect-ratio-selector__button"
				:class="{ 'is-active': option.value === value }"
				:disabled="disabled"
				@click="$emit('change', option.value)"
			>
				<span class="multi-format-aspect-ratio-selector__preview-column">
					<span
						class="multi-format-aspect-ratio-selector__preview"
						:style="{ aspectRatio: option.previewRatio, width: option.previewWidth }"
					/>
					<span class="multi-format-aspect-ratio-selector__ratio">
						{{ option.label }}
					</span>
				</span>
			</button>
		</div>
		<div
			v-if="selectedOption"
			class="multi-format-aspect-ratio-selector__selected-details"
		>
			<div class="multi-format-aspect-ratio-selector__selected-copy">
				{{ selectedOption.description }}
			</div>
			<div class="multi-format-aspect-ratio-selector__icons">
				<img
					v-for="platform in selectedOption.platforms"
					:key="platform.label"
					class="multi-format-aspect-ratio-selector__icon"
					:src="platform.icon"
					:alt="platform.label"
					:title="platform.label"
				>
			</div>
		</div>
	</div>
</template>

<script>
import Title from '@videotools/js/components/Title/Title';
import { GraphicAspectRatio } from '@shared/enums/GraphicAspectRatio';

const SOCIAL_MEDIA_ICON_BASE_PATH = '/images/social-media/';
const PLATFORM_ICONS = {
	browser:       { label: 'Website', icon: `${SOCIAL_MEDIA_ICON_BASE_PATH}icons8-browser-50.png` },
	facebook:      { label: 'Facebook', icon: `${SOCIAL_MEDIA_ICON_BASE_PATH}icons8-facebook-50.png` },
	instagram:     { label: 'Instagram', icon: `${SOCIAL_MEDIA_ICON_BASE_PATH}icons8-instagram-50.png` },
	tiktok:        { label: 'TikTok', icon: `${SOCIAL_MEDIA_ICON_BASE_PATH}icons8-tiktok-50.png` },
	youtube:       { label: 'YouTube', icon: `${SOCIAL_MEDIA_ICON_BASE_PATH}icons8-youtube-50.png` },
	youtubeShorts: { label: 'YouTube Shorts', icon: `${SOCIAL_MEDIA_ICON_BASE_PATH}icons8-youtube-shorts-50.png` },
};
const ASPECT_RATIO_DETAILS = {
	[GraphicAspectRatio.AR_9_16]: {
		titleKey:     'vertical',
		previewRatio: '9 / 16',
		previewWidth: '31px',
		platforms:    ['instagram', 'tiktok', 'youtubeShorts', 'facebook'],
	},
	[GraphicAspectRatio.AR_4_5]: {
		titleKey:     'feed_portrait',
		previewRatio: '4 / 5',
		previewWidth: '42px',
		platforms:    ['instagram', 'facebook'],
	},
	[GraphicAspectRatio.AR_1_1]: {
		titleKey:     'square',
		previewRatio: '1 / 1',
		previewWidth: '44px',
		platforms:    ['instagram', 'facebook'],
	},
	[GraphicAspectRatio.AR_16_9]: {
		titleKey:     'landscape',
		previewRatio: '16 / 9',
		previewWidth: '60px',
		platforms:    ['youtube', 'facebook', 'browser'],
	},
};

export default {
	name:       'MultiFormatAspectRatioSelector',
	components: { Title },
	props:      {
		value: {
			type:    String,
			default: null,
		},
		options: {
			type:    Array,
			default: () => [],
		},
		disabled: {
			type:    Boolean,
			default: false,
		},
		showHeader: {
			type:    Boolean,
			default: false,
		},
	},
	computed: {
		normalizedOptions() {
			return this.options.map((option) => {
				const details = ASPECT_RATIO_DETAILS[option.value];

				if (!details) {
					return {
						...option,
						title:        option.label,
						description:  '',
						previewRatio: '1 / 1',
						previewWidth: '44px',
						platforms:    [],
					};
				}

				return {
					...option,
					title:        this.$I18n.trans(`event.multiformat_editor.labels.formats.${details.titleKey}.title`),
					description:  this.$I18n.trans(`event.multiformat_editor.labels.formats.${details.titleKey}.description`),
					previewRatio: details.previewRatio,
					previewWidth: details.previewWidth,
					platforms:    details.platforms.map(platform => PLATFORM_ICONS[platform]).filter(Boolean),
				};
			});
		},
		selectedOption() {
			return this.normalizedOptions.find(option => option.value === this.value) ?? null;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.multi-format-aspect-ratio-selector {
	width: 100%;
}

.multi-format-aspect-ratio-selector__header {
	margin-bottom: $sp4;
}

.multi-format-aspect-ratio-selector__options {
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: $sp3;
}

.multi-format-aspect-ratio-selector__button {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 96px;
	padding-top: $sp3;
	padding-right: $sp3;
	padding-bottom: $sp3;
	padding-left: $sp3;
	border: 1px solid var(--color-grey-800);
	border-radius: var(--t-border-radius-card--lg);
	color: var(--color-grey-050);
	cursor: pointer;
	text-align: left;
	transition: border-color $trans-time-fast, background-color $trans-time-fast, opacity $trans-time-fast;
	overflow: hidden;

	body.-t-light & { // stylelint-disable-line
		border-color: var(--color-grey-900);
	}

	&:hover,
	&:focus {
		border-color: var(--color-grey-600);

		body.-t-light & { // stylelint-disable-line
			border-color: var(--color-grey-800);
		}
	}

	&.is-active {
		border-color: var(--color-hype-yellow);
		background-color: var(--color-grey-800);
		outline: 1px solid var(--color-hype-yellow);

		body.-t-light & { // stylelint-disable-line
			border-color: var(--color-hype-yellow);
			background-color: var(--color-grey-950);
		}
	}

	&:disabled {
		cursor: not-allowed;
		opacity: 0.5;
	}
}

.multi-format-aspect-ratio-selector__preview-column {
	display: flex;
	align-items: center;
	flex-direction: column;
	justify-content: center;
	height: 100%;
}

.multi-format-aspect-ratio-selector__preview {
	display: block;
	max-width: 100%;
	border: 1px solid var(--color-grey-300);
	border-radius: 4px;
	background-color: var(--color-grey-850);

	body.-t-light & { // stylelint-disable-line
		background-color: var(--color-grey-950);
	}
}

.multi-format-aspect-ratio-selector__ratio {
	@include font(primary, bold, normal, fs-60);
	margin-top: $sp3;
	color: var(--color-grey-050);
}

.multi-format-aspect-ratio-selector__selected-details {
	display: flex;
	flex-direction: column;
	gap: $sp2;
	margin-top: $sp4;
	padding-top: $sp3;
	padding-right: $sp4;
	padding-bottom: $sp3;
	padding-left: $sp4;
	border: 1px solid var(--color-grey-800);
	border-radius: var(--t-border-radius-card--lg);

	body.-t-light & { // stylelint-disable-line
		border-color: var(--color-grey-900);
	}
}

.multi-format-aspect-ratio-selector__selected-title {
	@include font(primary, medium, normal, fs-100);
	color: var(--color-grey-050);
}

.multi-format-aspect-ratio-selector__selected-copy {
	@include font(primary, book, normal, fs-100);
	color: var(--color-grey-300);
}

.multi-format-aspect-ratio-selector__icons {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: $sp2;
	margin-top: $sp1;
}

.multi-format-aspect-ratio-selector__icon {
	display: block;
	width: 32px;
	height: 32px;
	object-fit: contain;
}

</style>
